import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language?: string;
  height?: string;
  readOnly?: boolean;
  placeholder?: string;
}

/**
 * Code editor component using Monaco Editor.
 * Note: Requires @monaco-editor/react to be installed.
 * Install with: npm install @monaco-editor/react
 */
export const CodeEditor = ({
  value,
  onChange,
  language = 'plaintext',
  height = '400px',
  readOnly = false,
  placeholder = 'Enter code...',
}: CodeEditorProps) => {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Dynamic import of Monaco Editor
    const loadMonaco = async () => {
      try {
        const MonacoEditor = (await import('@monaco-editor/react')).default as any;
        
        if (containerRef.current && !editorRef.current) {
          // Monaco Editor will be rendered by the component
          editorRef.current = MonacoEditor;
        }
      } catch (error) {
        console.error('Failed to load Monaco Editor. Please install: npm install @monaco-editor/react', error);
      }
    };

    loadMonaco();
  }, []);

  // Fallback to textarea if Monaco is not available
  if (!editorRef.current) {
    return (
      <Box
        component="textarea"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        sx={{
          width: '100%',
          minHeight: height,
          fontFamily: 'monospace',
          fontSize: '14px',
          padding: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          resize: 'vertical',
        }}
      />
    );
  }

  // Use Monaco Editor if available
  const MonacoEditor = editorRef.current;
  return (
    <Box ref={containerRef} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <MonacoEditor
        height={height}
        language={language}
        value={value}
        onChange={(val: string | undefined) => onChange?.(val || '')}
        theme="vs-dark"
        options={{
          readOnly,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          fontSize: 14,
          lineNumbers: 'on',
          automaticLayout: true,
        }}
      />
    </Box>
  );
};
